export interface DocumentCodeOption {
    code: string;
    types: number[];
    carlines: number[];
    modelYears: number[];
    allVehicles: boolean;
}

export interface PreparedDocumentCode extends DocumentCodeOption {
    normalized: string;
}

export interface DocumentCodeFilter {
    types?: number[];
    carlines?: number[];
    modelYear?: number;
}

export const normalizeDocumentCode = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export const DOCUMENT_CODE_RESULT_LIMIT = 30;

export const DOCUMENT_CODE_MIN_QUERY = 2;

export const prepareDocumentCodes = (codes: DocumentCodeOption[]): PreparedDocumentCode[] =>
    codes.map((entry) => ({
        ...entry,
        carlines: entry.carlines ?? [],
        modelYears: entry.modelYears ?? [],
        allVehicles: Boolean(entry.allVehicles),
        normalized: normalizeDocumentCode(entry.code),
    }));

const matchRank = (normalized: string, query: string): number => {
    if (normalized === query) return 0;
    if (normalized.startsWith(query)) return 1;
    return 2;
};

const matchesFilter = (option: PreparedDocumentCode, filter: DocumentCodeFilter): boolean => {
    if (filter.types?.length && !option.types.some((type) => filter.types?.includes(type))) {
        return false;
    }
    if (option.allVehicles) {
        return true;
    }
    if (filter.carlines?.length && !option.carlines.some((carline) => filter.carlines?.includes(carline))) {
        return false;
    }
    if (filter.modelYear !== undefined && !option.modelYears.includes(filter.modelYear)) {
        return false;
    }
    return true;
};

const scope = (options: PreparedDocumentCode[], filter: DocumentCodeFilter = {}): PreparedDocumentCode[] =>
    options.filter((option) => matchesFilter(option, filter));

export const rankDocumentCodes = (
    options: PreparedDocumentCode[],
    query: string,
    filter: DocumentCodeFilter = {},
    limit: number = DOCUMENT_CODE_RESULT_LIMIT
): PreparedDocumentCode[] => {
    const normalizedQuery = normalizeDocumentCode(query);
    if (normalizedQuery.length < DOCUMENT_CODE_MIN_QUERY) return [];

    return scope(options, filter)
        .filter((option) => option.normalized.includes(normalizedQuery))
        .sort(
            (a, b) =>
                matchRank(a.normalized, normalizedQuery) - matchRank(b.normalized, normalizedQuery) ||
                a.code.localeCompare(b.code)
        )
        .slice(0, limit);
};

export const browseDocumentCodes = (
    options: PreparedDocumentCode[],
    filter: DocumentCodeFilter = {},
    limit: number = DOCUMENT_CODE_RESULT_LIMIT
): PreparedDocumentCode[] => scope(options, filter).slice(0, limit);

export const countDocumentCodes = (
    options: PreparedDocumentCode[],
    filter: DocumentCodeFilter = {},
    query = ''
): number => {
    const inScope = scope(options, filter);
    const normalizedQuery = normalizeDocumentCode(query);
    if (normalizedQuery.length < DOCUMENT_CODE_MIN_QUERY) return inScope.length;

    return inScope.filter((option) => option.normalized.includes(normalizedQuery)).length;
};

const codeCharPositions = (code: string): number[] => {
    const positions: number[] = [];
    for (let index = 0; index < code.length; index += 1) {
        if (normalizeDocumentCode(code[index]).length > 0) {
            positions.push(index);
        }
    }
    return positions;
};

export const documentCodeMatch = (code: string, query: string): [number, number] | null => {
    const normalizedQuery = normalizeDocumentCode(query);
    if (normalizedQuery.length < DOCUMENT_CODE_MIN_QUERY) return null;

    const at = normalizeDocumentCode(code).indexOf(normalizedQuery);
    if (at === -1) return null;

    const positions = codeCharPositions(code);
    return [positions[at], positions[at + normalizedQuery.length - 1] + 1];
};
